import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { faqItems } from "@/data/faq";

function FAQItem({ item, index, isOpen, onToggle }) {
  return (
    <div className="border-b border-violet-light last:border-0">
      <button
        onClick={onToggle}
        data-testid={`faq-trigger-${index}`}
        className="flex w-full items-center justify-between gap-4 py-6 text-left transition-colors hover:text-violet-fuerte"
        aria-expanded={isOpen}
      >
        <span className="font-display text-xl font-semibold text-ink">
          {item.question}
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className="shrink-0 text-ink-3"
        >
          <ChevronDown className="w-6 h-6" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <p
              data-testid={`faq-content-${index}`}
              className="pb-6 text-lg text-ink-2 leading-relaxed"
            >
              {item.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-20 md:py-32 bg-white border-t border-violet-light"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-12">
        <div className="grid items-start gap-12 lg:grid-cols-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.75 }}
            className="lg:col-span-4"
          >
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gold mb-4">
              Ayuda
            </p>
            <h2
              data-testid="faq-title"
              className="font-display text-3xl sm:text-4xl font-bold text-ink tracking-tight"
            >
              Preguntas frecuentes
            </h2>
            <p className="mt-5 text-xl text-ink-2 leading-relaxed">
              Si no ves tu duda, escríbenos: solemos responder el mismo día.
            </p>
            <div className="mt-8">
              <Link
                to="/contacto"
                data-testid="faq-contact-link"
                className="group inline-flex items-center gap-2 text-lg font-semibold text-violet-fuerte transition-colors hover:text-violet-oscuro"
              >
                Escribir al equipo
                <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>

          <div
            className="lg:col-span-8 rounded-2xl border border-violet-light bg-[#FAFAFE]/60 px-6 py-2 md:px-8 md:py-4"
            data-testid="faq-accordion"
          >
            {faqItems.map((item, i) => (
              <FAQItem
                key={i}
                item={item}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
